import React from "react";
import { Modal as AntModal } from "antd";

/**
 * Wrapper component for Ant Design Modal
 *
 * This wrapper allows us to easily replace Ant Design in the future without changing all imports.
 * It preserves all Ant Design Modal functionality including static methods for confirmation dialogs.
 *
 * @component
 * @example
 * import { Modal } from '../GeneralComponents/ui';
 *
 * // Basic Modal
 * <BaseModal
 *   title="Basic Modal"
 *   visible={visible}
 *   onOk={handleOk}
 *   onCancel={handleCancel}
 * >
 *   <p>Some contents...</p>
 * </BaseModal>
 *
 * // Static methods
 * Modal.info({ title: 'Info', content: 'This is an info message' });
 * Modal.success({ title: 'Success', content: 'This is a success message' });
 * Modal.error({ title: 'Error', content: 'This is an error message' });
 * Modal.warning({ title: 'Warning', content: 'This is a warning message' });
 * Modal.confirm({
 *   title: 'Confirm',
 *   content: 'Are you sure?',
 *   onOk: () => console.log('OK'),
 *   onCancel: () => console.log('Cancel')
 * });
 *
 * @see https://ant.design/components/modal/ - Ant Design Modal documentation
 */
const BaseModal = (props) => {
  // Create a unique ID for this modal instance to scope styles
  const modalId = React.useMemo(
    () => `modal-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  return (
    <React.Fragment>
      <AntModal
        {...props}
        icon={props.iconType}
        wrapClassName={`${modalId} ${props.wrapClassName || ""}`}
      />
      <style>
        {`
          body .${modalId} .ant-modal-content {
            border-radius: 16px;
            overflow: hidden;
          }
        `}
      </style>
    </React.Fragment>
  );
};

// Preserve Modal static methods
BaseModal.info = AntModal.info;
BaseModal.success = AntModal.success;
BaseModal.error = AntModal.error;
BaseModal.warning = AntModal.warning;
BaseModal.confirm = AntModal.confirm;
BaseModal.destroyAll = AntModal.destroyAll;

export default BaseModal;
